import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { toast } from 'src/components/ui/toast';
import { setGlobalError } from '../../features/notifications/redux';
import { parseApiError, isNetworkError, isTimeoutError, MFA_SESSION_EXPIRED_MESSAGE } from './errors';
import { NormalizedError } from './error_type';
import { RequestMeta, AppAxiosRequestConfig } from './types';
import { signOut } from 'firebase/auth';
import { auth } from '@/features/auth/firebase';
import { resolveTenantCode, toTenantPath } from 'src/lib/tenantRouting';
import { getCloudSessionId, getCloudDistinctId } from 'src/lib/posthog';

const env = import.meta.env;
const appApiUrl = env.VITE_API_URL;

// Axios instance with default error-handling for API requests.
//
// Default error-handling behavior (when no special config is provided):
// - All errors are intercepted and normalized (network, timeout, and API errors).
// - If the error context is 'auth' (401) or 'network' (code >= 500), a global error notification is shown (unless meta.globalError is false or meta.background is true).
// - The normalized error is attached to the rejected promise as error.parsed for local handling.
// - In development mode, detailed error logs are printed to the console.
//
// You can override this behavior per-request using the 'meta' config property.

// Extend AxiosRequestConfig to include our metadata
declare module 'axios' {
  interface AxiosRequestConfig {
    meta?: RequestMeta;
    skipTenantPrefix?: boolean;
  }
}

const axiosInstance = axios.create({
  baseURL: appApiUrl,
  headers: {
    Accept: 'application/json'
  },
  timeout: 50000 // 30 seconds timeout
});

// Request Interceptor - Add metadata and request ID
axiosInstance.interceptors.request.use(
  async (config: AppAxiosRequestConfig) => {
    // Generate request ID for tracking
    config.meta = {
      ...(config.meta || {}),
      requestId: config.meta?.requestId || `req_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      retryCount: config.meta?.retryCount || 0
    };

    // Prefix platform API requests with tenant when needed.
    if (typeof config.url === 'string' && !config.skipTenantPrefix) {
      const isAbsoluteUrl = /^(https?:)?\/\//.test(config.url);
      if (!isAbsoluteUrl) {
        const normalizedUrl = config.url.startsWith('/') ? config.url : `/${config.url}`;
        const tenantCode = typeof window !== 'undefined' ? resolveTenantCode(window.location.pathname) : null;
        config.url = toTenantPath(normalizedUrl, tenantCode);
      }
    }

    // Attach the PostHog session id so backend-side error tracking can link
    // an unhandled exception back to the browser session that caused it.
    const posthogSessionId = getCloudSessionId();
    if (posthogSessionId) {
      config.headers['X-POSTHOG-SESSION-ID'] = posthogSessionId;
    }
    // Attach the PostHog distinct id too — the backend prefers its own
    // auth-resolved id once a user is authenticated, but this covers
    // pre-auth requests and lets the backend log-check the two agree.
    const posthogDistinctId = getCloudDistinctId();
    if (posthogDistinctId) {
      config.headers['X-POSTHOG-DISTINCT-ID'] = posthogDistinctId;
    }

    // Wait until Firebase has restored the persisted session. On a refresh,
    // currentUser is null for a moment and requests would otherwise go out
    // unauthenticated and surface as an expired-session dialog.
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting Firebase token:', error);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    // Debug log to check the full config object
    if (process.env.NODE_ENV === 'development') {
      console.log('AXIOS INTERCEPTOR: config', error.config);
    }
    // Skip if request was aborted (e.g., due to route change)
    if (error?.config?.signal?.aborted) {
      return Promise.reject(error);
    }

    // Handle MFA session expired/invalid — sign the user out so they must
    // re-authenticate and complete MFA again on next login.
    // The signOut call triggers onAuthStateChanged in FirebaseContext which
    // handles the state transition and routing; no hard navigation needed.
    if (error.response?.status === 403 && (error.response?.data as any)?.detail === 'mfa_required') {
      try {
        await signOut(auth);
      } catch {
        // best-effort sign-out
      }
      toast.info(MFA_SESSION_EXPIRED_MESSAGE);
      return Promise.reject(error);
    }

    const config = error.config;
    const meta = config?.meta || {};

    // Parse and normalize the error
    let parsedError: NormalizedError;

    if (isNetworkError(error)) {
      parsedError = {
        code: 0,
        title: 'Network Error',
        description: 'Unable to connect to the server. Please check your internet connection.',
        context: 'network',
        recoverable: true,
        retryable: true,
        userActionable: true,
        timestamp: Date.now(),
        requestId: meta.requestId
      };
    } else if (isTimeoutError(error)) {
      parsedError = {
        code: 408,
        title: 'Request Timeout',
        description: 'The request took too long to complete. Please try again.',
        context: 'network',
        recoverable: true,
        retryable: true,
        userActionable: true,
        timestamp: Date.now(),
        requestId: meta.requestId
      };
    } else {
      parsedError = parseApiError(error.response);
      parsedError.requestId = meta.requestId;
      parsedError.originalError = error;
    }

    // Overwrite with meta.title/description if provided (applies to all error types)
    if (meta.title) parsedError.title = meta.title;
    if (meta.description) parsedError.description = meta.description;
    if (meta.context) parsedError.context = meta.context as typeof parsedError.context;

    // Attach parsed error for local use
    (error as any).parsed = parsedError;

    // Show toast for rate limit errors (429)
    if (parsedError.code === 429 && !meta.background) {
      toast.warning("You're doing that too fast. Please wait and retry in a couple of seconds.", {
        toastId: 'rate-limit-toast', // Prevent duplicate toasts
        autoClose: 5000
      });
    }

    // Show global error if not explicitly disabled, and if meta.globalError is true
    // or the error is auth-related (401) or a server/network failure (5xx).
    const shouldShowGlobal =
      meta.globalError !== false &&
      (meta.globalError === true ||
        (parsedError.context === 'auth' && parsedError.code === 401) ||
        (parsedError.context === 'network' && parsedError.code >= 500));

    if (shouldShowGlobal && !meta.background) {
      // Lazy-load store to avoid circular dependency (store -> rootReducer -> reducers -> axios -> store)
      const { store } = await import('../../app/store/store');
      store.dispatch(setGlobalError({ error: parsedError, actions: meta.actions }));
    }

    // Log errors for debugging (in development)
    if (process.env.NODE_ENV === 'development') {
      console.group(`API Error [${parsedError.code}]: ${parsedError.title}`);
      console.error('Description:', parsedError.description);
      console.error('Context:', parsedError.context);
      console.error('Request ID:', parsedError.requestId);
      console.error('Original Error:', error);
      console.groupEnd();
    }

    return Promise.reject(error);
  }
);

// Helper function for GET requests with metadata
export function apiGet<T = any>(
  url: string,
  config?: AxiosRequestConfig & { meta?: RequestMeta }
): Promise<AxiosResponse<T>> {
  return axiosInstance.request({ ...config, url, method: 'get' });
}

// Helper function for POST requests with metadata
export function apiPost<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig & { meta?: RequestMeta }
): Promise<AxiosResponse<T>> {
  return axiosInstance.post(url, data, config);
}

// Helper function for PUT requests with metadata
export function apiPut<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig & { meta?: RequestMeta }
): Promise<AxiosResponse<T>> {
  return axiosInstance.put(url, data, config);
}

// Helper function for DELETE requests with metadata
export function apiDelete<T = any>(
  url: string,
  config?: AxiosRequestConfig & { meta?: RequestMeta }
): Promise<AxiosResponse<T>> {
  return axiosInstance.delete(url, config);
}

// Helper function for PATCH requests with metadata
export function apiPatch<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig & { meta?: RequestMeta }
): Promise<AxiosResponse<T>> {
  return axiosInstance.patch(url, data, config);
}
